import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import getSymbolFromCurrency from "currency-symbol-map";

import instance from "api/apiClient";
import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Modal } from "components/Modal/Modal";
import Pagination from "components/Pagination";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { apiRoutes } from "configs/apiRoutes";
import { selectPlayerListLimit, playersListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { useAppDispatch, useAppSelector } from "store/index";
import { closeModal, openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { getPlayerItem } from "store/players/list/slice";
import {
  clearSelectPlayerResults,
  initSelectPlayer,
  resetSelectPlayer,
  SelectPlayerPurpose,
  SelectPlayerRow,
  setSelectPlayerFilters,
  setSelectPlayerResults,
  setSelectPlayerSearchError,
  setSelectPlayerSelected,
} from "store/payments/selectPlayer/slice";

import styles from "./SelectPlayerModal.module.scss";

const DEFAULT_ORDER = {
  id: "none",
  email: "none",
  country: "none",
  currency: "none",
  registeredAt: "none",
  balanceReal: "none",
  balanceBonus: "none",
  category: "none",
  riskStatus: "none",
  verificationStatus: "none",
};

const resolvePurpose = (value: string | null): SelectPlayerPurpose =>
  value === "confiscation" ? "confiscation" : "compensation";

const formatBalance = (balance: number | null, currency: string) => {
  if (balance === null) return "—";

  const symbol = getSymbolFromCurrency(currency) ?? "";
  return `${symbol} ${balance.toFixed(2)}`.trim();
};

const SelectPlayerModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const purpose = resolvePurpose(searchParams.get("purpose"));
  const selectPlayer = useAppSelector((store) => store.selectPlayer);

  const [isLoading, setIsLoading] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(selectPlayerListLimit);
  const requestIdRef = useRef(0);
  const currentLimitRef = useRef(selectPlayerListLimit);

  useEffect(() => {
    dispatch(initSelectPlayer({ purpose }));
  }, [dispatch, purpose]);

  const buildSearchPayload = ({
    page,
    searchUserId,
    searchEmail,
    searchNickname,
  }: {
    page: number;
    searchUserId: string;
    searchEmail: string;
    searchNickname: string;
  }) => {
    const trimmedUserId = searchUserId.trim();
    const trimmedEmail = searchEmail.trim();
    const trimmedNickname = searchNickname.trim();
    const parsedUserId = trimmedUserId ? Number(trimmedUserId) : NaN;

    return {
      limit: currentLimitRef.current,
      offset: page - 1,
      email: trimmedEmail === "" ? undefined : trimmedEmail,
      userIds:
        trimmedUserId && Number.isFinite(parsedUserId) ? [parsedUserId] : [],
      categories: [] as number[],
      riskStatuses: [] as number[],
      countries: [] as string[],
      currencies: [] as string[],
      registrationFrom: "",
      registrationTo: "",
      verificationStatuses: [] as number[],
      order: DEFAULT_ORDER,
      ...(trimmedNickname ? { nickname: trimmedNickname } : {}),
    };
  };

  const hasSearchCriteria = (
    searchUserId: string,
    searchEmail: string,
    searchNickname: string,
  ) =>
    Boolean(searchUserId.trim() || searchEmail.trim() || searchNickname.trim());

  const fetchPlayers = async ({
    page,
    searchUserId,
    searchEmail,
    searchNickname,
    showValidationErrors = false,
  }: {
    page: number;
    searchUserId: string;
    searchEmail: string;
    searchNickname: string;
    showValidationErrors?: boolean;
  }) => {
    if (!hasSearchCriteria(searchUserId, searchEmail, searchNickname)) {
      dispatch(clearSelectPlayerResults());
      return;
    }

    const trimmedUserId = searchUserId.trim();
    if (trimmedUserId && !/^\d+$/.test(trimmedUserId)) {
      if (showValidationErrors) {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("invalidUserId"),
            type: NotificationTypes.error,
          }),
        );
      }
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    dispatch(setSelectPlayerSearchError(false));

    try {
      const result = await instance.post(
        apiRoutes.playersList,
        buildSearchPayload({
          page,
          searchUserId,
          searchEmail,
          searchNickname,
        }),
      );

      if (requestId !== requestIdRef.current) return;

      const items = result?.data?.items ?? [];
      const mappedRows: SelectPlayerRow[] = items.map(
        (item: {
          id: number;
          nickname?: string;
          email?: string;
          currency?: string;
          balanceReal?: number | string;
          verificationStatus?: string;
          status?: string;
        }) => ({
          select: item.id,
          id: item.id,
          email: item.email?.trim() ? item.email : "—",
          nickname: item.nickname?.trim() ? item.nickname : "—",
          currency: item.currency ?? "—",
          balanceReal: (() => {
            if (item.balanceReal === undefined || item.balanceReal === null) {
              return null;
            }
            const parsed = Number(item.balanceReal);
            return Number.isFinite(parsed) ? parsed : null;
          })(),
          status: item.verificationStatus?.trim() || item.status?.trim() || "—",
        }),
      );

      dispatch(
        setSelectPlayerResults({
          rows: mappedRows,
          totalItems: result?.data?.totalItems ?? 0,
          totalPages: result?.data?.totalPages ?? 0,
          activePage: page,
        }),
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      dispatch(setSelectPlayerSearchError(true));
      dispatch(
        showNotification({
          title: t("error"),
          message: t("selectPlayerSearchError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    dispatch(resetSelectPlayer());
    navigate(location.pathname);
    dispatch(closeModal());
  };

  const handleReset = () => {
    dispatch(initSelectPlayer({ purpose, reset: true }));
  };

  const handleSearch = () => {
    fetchPlayers({
      page: 1,
      searchUserId: selectPlayer.userId,
      searchEmail: selectPlayer.email,
      searchNickname: selectPlayer.nickname,
      showValidationErrors: true,
    });
  };

  const handlePrevClick = () => {
    if (selectPlayer.activePage <= 1 || isLoading) return;
    fetchPlayers({
      page: selectPlayer.activePage - 1,
      searchUserId: selectPlayer.userId,
      searchEmail: selectPlayer.email,
      searchNickname: selectPlayer.nickname,
    });
  };

  const handleNextClick = () => {
    if (selectPlayer.activePage >= selectPlayer.totalPages || isLoading) return;
    fetchPlayers({
      page: selectPlayer.activePage + 1,
      searchUserId: selectPlayer.userId,
      searchEmail: selectPlayer.email,
      searchNickname: selectPlayer.nickname,
    });
  };

  const openCreateModal = async (selectedUserId: number) => {
    if (isOpening) return;

    setIsOpening(true);
    try {
      const result = await dispatch(getPlayerItem(selectedUserId));
      const player = result.payload as
        | {
            id?: number;
            nickname?: string;
            currency?: string;
            balanceReal?: number;
          }
        | undefined;

      if (!player?.id) {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("selectPlayerLoadError"),
            type: NotificationTypes.error,
          }),
        );
        return;
      }

      const nextModal =
        purpose === "confiscation"
          ? ModalTypes.createConfiscation
          : ModalTypes.createCompensation;

      navigate(
        `${location.pathname}?activeModal=${nextModal}&entityId=${selectedUserId}&returnTo=list&purpose=${purpose}`,
      );
      dispatch(openModal({ activeModal: nextModal }));
    } finally {
      setIsOpening(false);
    }
  };

  const handleNext = () => {
    if (selectPlayer.selectedUserId === null) return;
    openCreateModal(selectPlayer.selectedUserId);
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header className={styles.modalHeader}>
        <p>{t("selectPlayer")}</p>
      </Modal.Header>
      <Modal.Content className={styles.selectPlayerBody}>
        <p className={styles.subtitle}>{t("selectPlayerSearchHint")}</p>

        <form
          className={styles.searchPanel}
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
        >
          <div className={styles.filters}>
            <InnerInput
              type="text"
              label={t("playerId")}
              placeholder={t("enterUserId")}
              value={selectPlayer.userId}
              onChange={(value) =>
                dispatch(setSelectPlayerFilters({ userId: value }))
              }
              id="selectPlayerUserId"
              inputMode="numeric"
              disabled={isOpening}
            />
            <InnerInput
              type="text"
              label={t("email")}
              placeholder={t("enterEmail")}
              value={selectPlayer.email}
              onChange={(value) =>
                dispatch(setSelectPlayerFilters({ email: value }))
              }
              id="selectPlayerEmail"
              disabled={isOpening}
            />
            <InnerInput
              type="text"
              label={t("nickname")}
              placeholder={t("enterNickname")}
              value={selectPlayer.nickname}
              onChange={(value) =>
                dispatch(setSelectPlayerFilters({ nickname: value }))
              }
              id="selectPlayerNickname"
              disabled={isOpening}
            />
          </div>

          <div className={styles.filterActions}>
            <Button
              type="button"
              isSecondary
              className={styles.resetButton}
              txt={t("reset")}
              handleClick={handleReset}
              disabled={isOpening}
            />
            <Button
              type="submit"
              isGreen
              txt={t("search")}
              disabled={isOpening}
              isLoad={isLoading}
            />
          </div>
        </form>

        <div className={styles.results}>
          {selectPlayer.hasSearched && (
            <div className={styles.resultsHeader}>
              <p className={styles.resultsTitle}>
                {t("searchResults")}
                <span className={styles.resultsCount}>
                  ({selectPlayer.totalItems})
                </span>
              </p>
            </div>
          )}

          {isLoading ? (
            <div className={styles.resultsContent}>
              <div className={styles.tableContainer}>
                <DataTableSkeleton columnsCount={7} rowsCount={8} />
              </div>
              <div className={styles.resultsPagination}>
                <PaginationSkeleton />
              </div>
            </div>
          ) : selectPlayer.searchError ? (
            <div className={styles.errorState}>
              <EmptyState message={t("selectPlayerSearchError")} />
              <Button
                isGreen
                txt={t("retry")}
                handleClick={handleSearch}
                disabled={isOpening}
              />
            </div>
          ) : selectPlayer.hasSearched && selectPlayer.rows.length === 0 ? (
            <div className={styles.emptyResults}>
              {t("noPlayersFoundShort")}
            </div>
          ) : selectPlayer.rows.length > 0 ? (
            <div className={styles.resultsContent}>
              <div className={styles.tableContainer}>
                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th aria-label={t("selectPlayer")} />
                        <th>{t("playerId")}</th>
                        <th>{t("email")}</th>
                        <th>{t("nickname")}</th>
                        <th>{t("accountingCurrency")}</th>
                        <th>{t("realBalance")}</th>
                        <th>{t("status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectPlayer.rows.map((row) => {
                        const isSelected =
                          selectPlayer.selectedUserId === row.id;
                        const status = row.status?.toLowerCase() || "none";

                        return (
                          <tr
                            key={row.id}
                            className={
                              isSelected ? styles.selectedRow : undefined
                            }
                            onClick={() =>
                              dispatch(setSelectPlayerSelected(row.id))
                            }
                          >
                            <td>
                              <span
                                className={`${styles.radio} ${
                                  isSelected ? styles.radioChecked : ""
                                }`}
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={t("selectPlayer")}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  dispatch(setSelectPlayerSelected(row.id));
                                }}
                              />
                            </td>
                            <td>{row.id}</td>
                            <td className={styles.altText}>{row.email}</td>
                            <td className={styles.altText}>{row.nickname}</td>
                            <td>{row.currency}</td>
                            <td>
                              {formatBalance(row.balanceReal, row.currency)}
                            </td>
                            <td>
                              <span className={`badge badge--${status}`}>
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={styles.resultsPagination}>
                <Pagination
                  activePage={selectPlayer.activePage}
                  totalItems={selectPlayer.totalItems}
                  totalPages={selectPlayer.totalPages}
                  limit={currentLimit}
                  handlePrevClick={handlePrevClick}
                  handleNextClick={handleNextClick}
                  limitsArray={playersListLimits}
                  handleLimitClick={(limit) => {
                    currentLimitRef.current = limit;
                    setCurrentLimit(limit);
                    fetchPlayers({
                      page: 1,
                      searchUserId: selectPlayer.userId,
                      searchEmail: selectPlayer.email,
                      searchNickname: selectPlayer.nickname,
                    });
                  }}
                />
              </div>
            </div>
          ) : (
            <p className={styles.empty}>{t("selectPlayerSearchHint")}</p>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          isSecondary
          txt={t("cancel")}
          handleClick={handleClose}
          disabled={isOpening}
        />
        <Button
          isGreen
          txt={t("next")}
          handleClick={handleNext}
          disabled={selectPlayer.selectedUserId === null || isOpening}
          isLoad={isOpening}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default SelectPlayerModal;
